/**
 * stepRegistry.jsx
 *
 * Maps every step ID → render function.
 * Uses React.lazy for code-splitting — each step loads only when visited.
 *
 * Props passed to every step:
 *   { formData, setFormData, errors, touched, handlers,
 *     enabledOptionalSections, onToggleOptionalSection,
 *     aiHandlers }   ← NEW: AI engine callbacks
 */

import React, { lazy, Suspense } from 'react';

const PersonalStep           = lazy(() => import('./steps/PersonalStep'));
const SummaryStep            = lazy(() => import('./steps/SummaryStep'));
const SkillsExpertiseStep    = lazy(() => import('./steps/SkillsExpertiseStep'));
const ExperienceProjectsStep = lazy(() => import('./steps/ExperienceProjectsStep'));
const CredentialsStep        = lazy(() => import('./steps/CredentialsStep'));

const StepFallback = () => (
  <div className="flex items-center justify-center py-16">
    <div className="animate-spin rounded-full h-8 w-8 border-2 border-blue-600 border-t-transparent" />
  </div>
);

const STEP_REGISTRY = {
  personal: (p) => (
    <PersonalStep
      formData={p.formData}
      setFormData={p.setFormData}
      errors={p.errors}
      touched={p.touched}
      handleBlur={p.handlers.handleBlur}
      warnings={p.warnings}
      fieldLengths={p.fieldLengths}
      maxLengths={p.maxLengths}
    />
  ),

  summary: (p) => (
    <SummaryStep
      formData={p.formData}
      setFormData={p.setFormData}
      aiHandlers={p.aiHandlers}
      isPremium={p.isPremium}
      isAiLimitReached={p.isAiLimitReached}
    />
  ),

  'skills-expertise': (p) => (
    <SkillsExpertiseStep
      formData={p.formData}
      toggleSkill={p.handlers.toggleSkill}
      toggleTool={p.handlers.toggleTool}
      toggleCompetency={p.handlers.toggleCompetency}
      injectKeyword={p.handlers.injectKeyword}
      aiHandlers={p.aiHandlers}
      isPremium={p.isPremium}
      isAiLimitReached={p.isAiLimitReached}
    />
  ),

  'experience-projects': (p) => (
    <ExperienceProjectsStep
      formData={p.formData}
      updateItem={p.handlers.updateItem}
      addItem={p.handlers.addItem}
      removeItem={p.handlers.removeItem}
      addAchievement={p.handlers.addAchievement}
      removeAchievement={p.handlers.removeAchievement}
      updateAchievement={p.handlers.updateAchievement}
      aiHandlers={p.aiHandlers}
      isPremium={p.isPremium}
      isAiLimitReached={p.isAiLimitReached}
    />
  ),

  credentials: (p) => (
    <CredentialsStep
      formData={p.formData}
      updateItem={p.handlers.updateItem}
      addItem={p.handlers.addItem}
      removeItem={p.handlers.removeItem}
      enabledOptionalSections={p.enabledOptionalSections}
      onToggleOptionalSection={p.onToggleOptionalSection}
    />
  ),
};

export function renderStep(stepId, props) {
  console.log('[StepRegistry] renderStep called:', {
    stepId,
    availableSteps: Object.keys(STEP_REGISTRY),
    propsKeys: Object.keys(props || {}),
    formDataKeys: Object.keys(props?.formData || {}),
    hasHandlers: !!props?.handlers,
    hasAiHandlers: !!props?.aiHandlers
  });
  
  const render = STEP_REGISTRY[stepId];
  if (!render) {
    console.error('[StepRegistry] Step not found:', stepId, '- Available:', Object.keys(STEP_REGISTRY));
    return (
      <div className="flex items-center justify-center py-16">
        <div className="text-center">
          <p className="text-red-500 text-sm mb-2">Step "{stepId}" not found</p>
          <p className="text-gray-500 text-xs">Available steps: {Object.keys(STEP_REGISTRY).join(', ')}</p>
        </div>
      </div>
    );
  }

  console.log('[StepRegistry] Rendering step:', stepId);
  return (
    <Suspense fallback={<StepFallback />}>
      {render(props)}
    </Suspense>
  );
}

export default STEP_REGISTRY;
